import { observable, action, reaction, computed, extendObservable } from "mobx";
import Api from "~/service/Api";
import PhotoEditingImage from "~/models/PhotoEditing/PhotoEditingImage";
import PhotoEditingPhotoRepository from "~/repository/PhotoEditing/PhotoEditingPhotoRepository";

class ReviewImagesStore {
  /**
   * @property {boolean}
   */
  @observable isLoading = false;

  /**
   * @property {boolean}
   */
  @observable isSavingPhotosPerImage = false;

  /**
   * @property {boolean} isSaving used when hitting /group-photos
   */
  @observable isSaving = false;

  /**
   * @property {number}
   */
  @observable photosPerImage = 0;

  /**
   * @property {boolean}
   */
  @observable hasPhotosPerImageErrors = false;

  /**
   * @property {boolean}
   */
  @observable groupedByPhotosPerImage = false;

  /**
   * @property {Array}
   */
  @observable photoEditingImages = [];

  /**
   * @type {string}
   */
  @observable sortingMethod = "exif-datetime";

  /**
   * @property {number|null}
   */
  photoEditingJobId = null;

  /**
   * @property {PhotoEditingPhotoRepository}
   */
  photoEditingPhotoRepository = null;

  @computed
  get isExifCompatible() {
    // Each photo must have a value for exifDts and exifExposureTime to be compatible
    return !this.photoEditingImages.some(photoEditingImage =>
      photoEditingImage.photoEditingPhotos.some(
        photoEditingPhoto => !photoEditingPhoto.exifDts || !photoEditingPhoto.exifExposureTime,
      ),
    );
  }

  @computed
  get hasHdrWarnings() {
    return this.photoEditingImages.some(photoEditingImage =>
      photoEditingImage.photoEditingPhotos.some(photoEditingPhoto => !photoEditingPhoto.isHdrCapable),
    );
  }

  @computed
  get hasSelectedPhotos() {
    return this.photoEditingImages.some(photoEditingImage =>
      photoEditingImage.photoEditingPhotos.some(photoEditingPhoto => photoEditingPhoto.isSelected),
    );
  }

  @computed
  get hasPhotos() {
    return this.getAllPhotos().length > 0;
  }

  constructor(photoEditingJobId) {
    this.photoEditingJobId = photoEditingJobId;
    this.photoEditingPhotoRepository = new PhotoEditingPhotoRepository();
  }

  @action
  load() {
    this.isLoading = true;

    return Api.get(`/photo-editing/jobs/${this.photoEditingJobId}`).then(response => {
      this.processIncomingJob(response.data.photoEditingJob);
      // Register reaction, whenever the images position change then we'll trigger a save.
      // Dispose any previous registered reaction.
      this.reaction && this.reaction.dispose();
      this.reaction = reaction(
        () => this.photoEditingImages,
        () => this.save(),
      );

      this.isLoading = false;
    });
  }

  @action
  processIncomingJob(photoEditingJob) {
    // we only need a subset of the job (for now).
    const { photoEditingImages = [], photosPerImage } = photoEditingJob;

    this.photosPerImage = photosPerImage;

    // The user won't be able to continue until they group photos, but we need to make sure we don't block them
    // if they had already grouped photos in the past.
    if (photosPerImage > 0) {
      this.groupedByPhotosPerImage = true;
    }

    photoEditingImages.forEach(photoEditingImageData => {
      const photoEditingImage = new PhotoEditingImage(photoEditingImageData);
      // decorate each photo so we can track if they are selected in the UI
      photoEditingImage.photoEditingPhotos.forEach(photoEditingPhoto => {
        extendObservable(photoEditingPhoto, {
          isSelected: false,
        });
      });

      this.photoEditingImages.push(photoEditingImage);
    });

    this.photoEditingImages = this.photoEditingImages.slice().sort((a, b) => (a.creationDts > b.creationDts ? 1 : -1));

    this.ensureOneEmptyImage();

    // If no grouping is done yet, try the automatic grouping
    if (photosPerImage === 0 && this.groupPhotos("automatic")) {
      // Reaction is not setup at that time, so we need to manually save
      // @todo: there is probably a better way to handle this...
      this.save();
    }
  }

  /**
   * Make sure the last image in the collection is empty. If all images have photos then we'll create a new transient
   * image so the user can
   */
  @action
  ensureOneEmptyImage() {
    // first remove any image without photos
    const newPhotoEditingImages = this.photoEditingImages.filter(
      photoEditingImage => photoEditingImage.photoEditingPhotos.length > 0,
    );

    newPhotoEditingImages.push(new PhotoEditingImage({}));
    this.photoEditingImages = newPhotoEditingImages;
  }

  @action
  reorderPhoto({ sourceImageId, sourcePhotoIndex }, { destinationImageId, destinationPhotoIndex }) {
    const sourceImage = this.photoEditingImages.find(image => image.photoEditingImageId === sourceImageId);
    const destinationImage = this.photoEditingImages.find(image => image.photoEditingImageId === destinationImageId);
    const sourcePhoto = sourceImage.photoEditingPhotos[sourcePhotoIndex];

    // note that source and destination images can point to the same image.

    sourceImage.removePhotoAtIndex(sourcePhotoIndex);
    destinationImage.addPhotoAtIndex(sourcePhoto, destinationPhotoIndex);

    this.ensureOneEmptyImage();
  }

  @action
  updatePhotosPerImage(photosPerImage) {
    this.photosPerImage = photosPerImage;
  }

  @action
  updateSortingMethod(sortingMethod) {
    this.sortingMethod = sortingMethod;
  }

  @action
  setHasPhotosPerImageErrors(value) {
    this.hasPhotosPerImageErrors = value;
  }

  @action groupPhotos(mode) {
    if (mode === "photosPerImage" && !this.photosPerImage) {
      return false;
    }

    if (mode === "automatic" && !this.isExifCompatible) {
      return false;
    }

    this.isSavingPhotosPerImage = true;

    // save user's selection.
    // todo: also save if the user is using auto-grouping...
    Api.post(`/photo-editing/jobs/${this.photoEditingJobId}`, {
      photosPerImage: this.photosPerImage,
    }).finally(() => {
      this.isSavingPhotosPerImage = false;
    });

    // Stack all photos in a single array.
    const allPhotos = this.getAllPhotos();

    // Automatic mode will always take the exif-datetime method
    // Manual mode can be either exif-datetime or filename
    if (this.sortingMethod === "exif-datetime" || mode === "automatic") {
      // Sort by Exif Dates
      // If the images are not exif compatible, it wont be sorted
      if (this.isExifCompatible) {
        allPhotos.sort((photoA, photoB) => (photoA.exifDts > photoB.exifDts ? 1 : -1));
      }
    } else {
      // Sort by filename
      allPhotos.sort((photoA, photoB) => {
        // Try to find numbers in the filename to compare them
        const photoANumber = photoA.fileName.match(/\d+/g);
        const photoBNumber = photoB.fileName.match(/\d+/g);

        // Match will be null is not found
        if (photoANumber && photoBNumber) {
          // Take first match of each photo
          return Number(photoANumber[0]) > Number(photoBNumber[0]) ? 1 : -1;
        }

        return 0;
      });
    }

    // clear images so they can be reassigned as we see fit.
    this.photoEditingImages.forEach(image => image.photoEditingPhotos.clear());

    // What will end up as the new photoEditingImages
    const newPhotoEditingImages = [];

    // allPhotos array will be reduced in each loop, and extracted photos will be assigned to images as we see fit.
    while (allPhotos.length > 0) {
      // grab an existing image, or create a new one
      const image = this.photoEditingImages.shift() || new PhotoEditingImage({});

      let photosForThisImage = 0;

      if (mode === "photosPerImage") {
        photosForThisImage = this.photosPerImage;
      } else if (mode === "automatic") {
        if (allPhotos.length === 1) {
          // There is only one photo remaining, nothing else to do
          photosForThisImage = 1;
        } else {
          // Detect each new image by comparing the date between shoots + the exposure time + a threshold
          // Note that the threshold is 4 seconds and might not work for all cases (ex.: taking many HDR shoots too quickly or waiting too long between exposures)
          // We use n + n+1 exposure time to create the threshold. Note that this is still an experimentation and may not work in all cases
          const allowedThreshold = 4;
          let maxTimeDifference = allPhotos[0].exifExposureTime + allowedThreshold;
          photosForThisImage = 1;

          if (allPhotos.length > 1) {
            maxTimeDifference += allPhotos[1].exifExposureTime;
          }

          while (
            photosForThisImage < allPhotos.length &&
            allPhotos[photosForThisImage].exifDts - allPhotos[photosForThisImage - 1].exifDts <= maxTimeDifference
          ) {
            maxTimeDifference = allPhotos[photosForThisImage].exifExposureTime + allowedThreshold;

            if (allPhotos.length > photosForThisImage + 1) {
              maxTimeDifference += allPhotos[photosForThisImage + 1].exifExposureTime;
            }

            photosForThisImage += 1;
          }
        }
      }

      // Take out from the array the necessary number of photos that will go in this image.
      allPhotos.splice(0, photosForThisImage).forEach(photo => image.photoEditingPhotos.push(photo));

      newPhotoEditingImages.push(image);
    }

    this.photoEditingImages = newPhotoEditingImages;
    this.ensureOneEmptyImage();
    this.groupedByPhotosPerImage = true;
    // remove the red highlight from the dropdown if any
    this.hasPhotosPerImageErrors = false;

    return true;
  }

  static getPhotosPerImagesOptions() {
    const options = [];

    for (let i = 2; i <= 24; i += 1) {
      options.push({ key: i, value: i, text: i });
    }

    return options;
  }

  static getSortingMethods() {
    return [
      { key: "exif-datetime", value: "exif-datetime", text: "Datetime" },
      { key: "filename", value: "filename", text: "Filename" },
    ];
  }

  @action
  deleteSelectedPhotoEditingPhotos() {
    if (this.isLoading) {
      return Promise.resolve();
    }

    this.isLoading = true;

    const selectedPhotos = this.getAllPhotos().filter(photoEditingPhoto => photoEditingPhoto.isSelected);

    return this.photoEditingPhotoRepository
      .batchDelete(selectedPhotos.map(photoEditingPhoto => photoEditingPhoto.photoEditingPhotoId))
      .then(() => {
        // Remove deleted photos from the UI
        selectedPhotos.forEach(photoEditingPhoto => {
          for (let i = 0; i < this.photoEditingImages.length; i += 1) {
            const photoEditingImage = this.photoEditingImages[i];
            const indexOf = photoEditingImage.photoEditingPhotos.indexOf(photoEditingPhoto);

            if (indexOf !== -1) {
              photoEditingImage.removePhotoAtIndex(indexOf);
            }
          }
        });
        // Ensure an empty image section
        this.ensureOneEmptyImage();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  selectAllImagesWithWarnings() {
    const photoEditingImagesWithWarnings = this.getAllPhotos().filter(
      photoEditingPhoto => !photoEditingPhoto.isHdrCapable,
    );

    photoEditingImagesWithWarnings.forEach((photoEditingPhoto, index) => {
      photoEditingImagesWithWarnings[index].isSelected = true;
    });
  }

  @action
  togglePhotoEditingPhotoSelection(photoEditingPhotoId) {
    const photoEditingPhoto = this.getAllPhotos().find(
      photoEditingPhotoInLoop => photoEditingPhotoInLoop.photoEditingPhotoId === photoEditingPhotoId,
    );

    if (!photoEditingPhoto) {
      throw new Error(`Could not find photo with id ${photoEditingPhotoId}`);
    }

    photoEditingPhoto.isSelected = !photoEditingPhoto.isSelected;
  }

  @action
  clearPhotoEditingPhotosSelection() {
    const allPhotos = this.getAllPhotos();

    for (let i = 0; i < allPhotos.length; i += 1) {
      const photoEditingPhoto = allPhotos[i];
      photoEditingPhoto.isSelected = false;
    }
  }

  getAllPhotos() {
    const photos = [];

    for (let i = 0; i < this.photoEditingImages.length; i += 1) {
      const photoEditingImage = this.photoEditingImages[i];

      for (let j = 0; j < photoEditingImage.photoEditingPhotos.length; j += 1) {
        photos.push(photoEditingImage.photoEditingPhotos[j]);
      }
    }

    return photos;
  }

  @action
  save() {
    // prepare payload
    const payload = [];

    this.photoEditingImages.forEach(photoEditingImage => {
      // only send image info if it has photos.
      if (photoEditingImage.photoEditingPhotos.length === 0) {
        return;
      }

      const imagePayload = {
        imageId: photoEditingImage.photoEditingImageId,
        photos: photoEditingImage.photoEditingPhotos.map(photoEditingPhoto => photoEditingPhoto.photoEditingPhotoId),
      };

      payload.push(imagePayload);
    });

    if (payload.length === 0) {
      return Promise.resolve();
    }

    this.isSaving = true;

    return Api.post(`/photo-editing/jobs/${this.photoEditingJobId}/group-photos`, {
      images: payload,
    }).finally(() => {
      this.isSaving = false;
    });
  }

  @computed
  get notEnoughExposuresPerImage() {
    for (let i = 0; i < this.photoEditingImages.length; i += 1) {
      // Only check images with photos
      // 1 or 2 exposures are not enough
      if (
        this.photoEditingImages[i].photoEditingPhotos.length === 1 ||
        this.photoEditingImages[i].photoEditingPhotos.length === 2
      ) {
        return true;
      }
    }

    return false;
  }
}

export default ReviewImagesStore;
